import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {useCreateWebSettings} from "@/api/onboarding/create-web-settings.ts";
import {useUpdateWebSettings} from "@/api/onboarding/update-web-settings.ts";
import {useEffect} from "react";

// Define the schema with Zod
const formSchema = z.object({
    wordpress_consumer_key: z.string().min(1, "WordPress Consumer Key is required"),
    wordpress_consumer_secret: z.string().optional(),
    website_url: z.string().min(1, "Site URL is required"),
});

type Props = {
    id: string | undefined | null;
    wordpress_consumer_key: string | null | undefined;
    wordpress_consumer_secret: string | null | undefined;
    website_url: string | null | undefined;
    mode?: string;
    locator?: string;
}

// Define form data type based on schema
type FormData = z.infer<typeof formSchema>;

const WooCommerceWebSettingsForm: React.FC<Props> = ({id, website_url, wordpress_consumer_secret, wordpress_consumer_key, mode, locator}) => {
    // Initialize the form with react-hook-form and Zod resolver
    const form = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            wordpress_consumer_key: wordpress_consumer_key || "",
            wordpress_consumer_secret: wordpress_consumer_secret || "",
            website_url: website_url || "",
        },
    });

    useEffect(() => {
        if(id) {
            form.setValue("wordpress_consumer_key", wordpress_consumer_key || "");
            form.setValue("wordpress_consumer_secret", wordpress_consumer_secret || "");
            form.setValue("website_url", website_url || "");

        }
    }, [id])

    const {mutate: createWebSettings} = useCreateWebSettings()
    const {mutate: updateWebSettings} = useUpdateWebSettings()

    // Handle form submission
    const onSubmit = (data: FormData) => {
        if(!id) {
            createWebSettings({...data, website_type: "wordpress", mode, locator})
            return
        }
        updateWebSettings({...data, website_type: "wordpress", mode, locator})
        return
    };

    return (
        <div className="my-4">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 grid grid-cols-2 gap-4">
                    <FormField
                        control={form.control}
                        name="website_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Website URL *</FormLabel>
                                <FormControl>
                                    <Input
                                        className={"py-6"}
                                        placeholder="Enter Website URL"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="wordpress_consumer_key"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>WordPress Consumer Key *</FormLabel>
                                <FormControl>
                                    <Input
                                        className={"py-6"}
                                        placeholder="Enter WordPress consumer key"
                                        {...field}
                                        required
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="wordpress_consumer_secret"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>WordPress Consumer Secret</FormLabel>
                                <FormControl>
                                    <Input
                                        className={"py-6"}
                                        placeholder="Enter WordPress consumer secret"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <div className={"col-span-2"}>
                        <Button type="submit">
                            Save Configuration
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
}

export  default  WooCommerceWebSettingsForm;